import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { DealRoomSidebarTop } from '@/components/DealRoom/DealRoomSidebarTop';
import {
  isStageCompleted,
  stageLabelFor,
  stageSubLabel,
} from '@/components/Property/propertyCardConstants';
import { displayStepNumber } from '@/utils/propertyView/stageResolver';
import type { PropertyStage, PropertyView } from '@/types/PropertyView';

interface DealRoomSidebarProps {
  property: PropertyView;
  currentStage: number;
}

const ICON_ACTIVE = 'fa-light fa-circle-dot';
const ICON_DONE = 'fa-solid fa-circle-check';
const ICON_LOCKED = 'fa-light fa-lock';

export const DealRoomSidebar = ({
  property,
  currentStage,
}: DealRoomSidebarProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      className="taste-rise"
      sx={{
        width: 280,
        flexShrink: 0,
        borderRight:
          '1px solid color-mix(in srgb, var(--outline-variant) 55%, transparent)',
        backgroundColor: 'var(--panel-tonal)',
        py: 0,
      }}
    >
      <DealRoomSidebarTop property={property} />
      <List dense disablePadding sx={{ py: 1 }}>
        {property.visibleStages.map((stage) => {
          const active = stage === (currentStage as PropertyStage);
          // Completion comes from the property's real progress — not from
          // which stage is being viewed (a deep link to Stage 5 used to mark
          // Apply and Tour done with no documents uploaded).
          const completed =
            !active &&
            (isStageCompleted(stage, property) || stage < property.stage);
          const locked = !completed && !active && stage > property.stage;
          const subLabel = stageSubLabel(stage, property);
          const onClick = () => {
            if (locked) return;
            navigate(`/properties/${property.id}/stage/${stage}`);
          };

          const iconClass = completed
            ? ICON_DONE
            : active
            ? ICON_ACTIVE
            : locked
            ? ICON_LOCKED
            : ICON_ACTIVE;

          const color = completed
            ? 'var(--stage-completed)'
            : active
            ? property.isIdle
              ? 'var(--stage-idle)'
              : 'var(--stage-active)'
            : theme.palette.text.disabled;

          return (
            <ListItemButton
              key={stage}
              onClick={onClick}
              disabled={locked}
              selected={active}
              sx={{
                py: 1.25,
                px: 2,
                '&.Mui-selected': {
                  backgroundColor:
                    'color-mix(in srgb, var(--stage-active) 12%, transparent)',
                  borderLeft: '3px solid var(--stage-active)',
                  pl: 1.625,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, color }}>
                <i className={iconClass} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: active || completed ? 700 : 500,
                      color: active
                        ? theme.palette.text.primary
                        : theme.palette.text.secondary,
                    }}
                  >
                    {displayStepNumber(property, stage)}.{' '}
                    {stageLabelFor(property, stage)}
                  </Typography>
                }
                secondary={
                  subLabel ? (
                    <Typography variant="caption" color="text.secondary">
                      {subLabel}
                    </Typography>
                  ) : undefined
                }
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};
